import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { adminBaseQuery } from './utils'

interface User {
  id: string
  name: string
  email: string
}

interface Shipment {
  id: string
  address: string
  city: string
  country: string
  label_url: string
  name: string
  postal_code: string
  tracking_code: string
}

interface PaymentDetails {
  id: string
  name: string
  iban: string
  bank_name: string
}
export interface OrderListItem {
  id: number
  user: User
  updated_at: string
  created_at: string
  status: string
  promo_code: string
}

export interface OrderListItemCsv {
  id: number
  user: User
  updated_at: string
  created_at: string
  status: string
  promo_code: string
}

interface OrderItem {
  id: string
  manufacturer: string
  price: number
  amount_paid: number
  product_model: string
  purchased_date: string
  purchased_store: string
  serial_number: string
  sku: string
  working_condition: boolean
  promo_code: string
}

interface GetOrderResponse {
  files: Array<{ url: string; type?: string }>
  order_id: string
  order_item: OrderItem
  payment_details: PaymentDetails
  shipment: Shipment
  status: string
  promo_code: string
  updated_at: string
  created_at: string
  zendesk_url: string
  user: User
}

interface GetCSVParams {
  /** Required format is YYYY-MM-DD */
  start_date?: string
  /** Required format is YYYY-MM-DD */
  end_date?: string
}

export const backofficeApi = createApi({
  reducerPath: 'backofficeApi',
  baseQuery: fetchBaseQuery(adminBaseQuery),
  tagTypes: ['AdminOrder'],

  endpoints: builder => ({
    getCsvOrderList: builder.query<string, GetCSVParams>({
      query: ({ start_date, end_date }) => ({
        url: '/v1/admin/orders/csv',
        params: { start_date, end_date },
        /** Tells RTK the response will be text https://redux-toolkit.js.org/rtk-query/api/fetchBaseQuery#parsing-a-response */
        responseHandler: res => res.text(),
      }),
    }),
    // Order List endpoint
    getOrderList: builder.query<OrderListItem[], void>({
      query: () => `/v1/admin/orders`,
      transformResponse: (res: { data: OrderListItem[] }) => res.data,
    }),
    getOrder: builder.query<GetOrderResponse, string>({
      query: id => `/v1/admin/orders/${id}`,
      providesTags: ['AdminOrder'],
    }),
    // update order status
    updateOrderStatus: builder.mutation<GetOrderResponse, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/v1/admin/orders/${id}/status`,
        method: 'PUT',
        body: { status }, // Send the new status in the request body
      }),
      invalidatesTags: [{ type: 'AdminOrder' }],
    }),
    updateOrderAmountPaid: builder.mutation<
      GetOrderResponse,
      { order_id: string; order_item_id: string; amount_paid: number }
    >({
      query: ({ order_id, order_item_id, amount_paid }) => ({
        url: `/v1/admin/orders/${order_id}/order-items/${order_item_id}`,
        method: 'PATCH',
        body: { amount_paid }, // Send the amount_paid in the request body
      }),
      invalidatesTags: [{ type: 'AdminOrder' }],
    }),
  }),
})

export const {
  useGetOrderListQuery,
  useGetCsvOrderListQuery,
  useLazyGetCsvOrderListQuery,
  useGetOrderQuery,
  useUpdateOrderStatusMutation,
  useUpdateOrderAmountPaidMutation,
} = backofficeApi
